import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Image, Upload, Heart, Play, Pause, ChevronLeft, ChevronRight,
  Plus, Folder, Star, Search, X, Loader2, Sparkles, RefreshCw,
  Users, Tag, Calendar, MapPin, Maximize2, Grid, List, Trash2
} from "lucide-react";

/**
 * Family Photo Gallery with AI Captions
 * Photo sharing, organization, and slideshow for Memory Journal
 */
export default function FamilyPhotoGallery() {
  const [loading, setLoading] = useState(true);
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  
  const fileInputRef = useRef(null);

  const loadAlbums = useCallback(async () => {
    try {
      const response = await api.get("/photo-sharing/albums");
      setAlbums(response.data.albums || []);
    } catch (error) {
      console.error("Error loading albums:", error);
    }
  }, []);

  const loadPhotos = useCallback(async (albumId) => {
    try {
      const response = await api.get(`/photo-sharing/albums/${albumId}`);
      setPhotos(response.data.photos || []);
      setSelectedAlbum(response.data.album);
    } catch (error) {
      console.error("Error loading photos:", error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadAlbums();
      setLoading(false);
    };
    init();
  }, [loadAlbums]);

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const albumId = selectedAlbum?.id || "memories";

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("album_id", albumId);
        formData.append("generate_ai_caption", "true");

        const response = await api.post("/photo-sharing/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (response.data.success) {
          toast.success(
            response.data.ai_caption_generated 
              ? "Photo uploaded with AI caption!" 
              : "Photo uploaded!"
          );
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    setShowUpload(false);
    if (selectedAlbum) {
      loadPhotos(selectedAlbum.id);
    }
    loadAlbums();
  };

  const toggleFavorite = async (photoId, currentState) => {
    try {
      await api.put(`/photo-sharing/photos/${photoId}`, {
        is_favorite: !currentState
      });
      setPhotos(prev => prev.map(p => 
        p.id === photoId ? { ...p, is_favorite: !currentState } : p
      ));
      toast.success(currentState ? "Removed from favorites" : "Added to favorites");
    } catch (error) {
      toast.error("Failed to update favorite");
    }
  };

  const regenerateCaption = async (photoId) => {
    try {
      const response = await api.post(`/photo-sharing/photos/${photoId}/regenerate-caption`);
      if (response.data.success) {
        setPhotos(prev => prev.map(p => 
          p.id === photoId ? { ...p, caption: response.data.caption } : p
        ));
        toast.success("Caption regenerated!");
      }
    } catch (error) {
      toast.error("Failed to regenerate caption");
    }
  };

  const deletePhoto = async (photoId) => {
    if (!confirm("Are you sure you want to delete this photo?")) return;
    
    try {
      await api.delete(`/photo-sharing/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setSelectedPhoto(null);
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const filteredPhotos = photos.filter(photo => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      photo.caption?.toLowerCase().includes(term) ||
      photo.tags?.some(t => t.toLowerCase().includes(term)) ||
      photo.people?.some(p => p.toLowerCase().includes(term))
    );
  });

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A7C6F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="family-photo-gallery">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-500 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <Image className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Family Photo Gallery</h1>
              <p className="text-white/80">Cherished moments with AI-powered captions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowSlideshow(true)}
              disabled={photos.length === 0}
              className="bg-white/20 hover:bg-white/30 text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              Slideshow
            </Button>
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-white text-amber-600 hover:bg-white/90"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Photos
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Albums Sidebar */}
        <div className="lg:col-span-1">
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Folder className="w-5 h-5 text-amber-500" />
                Albums
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {albums.map(album => (
                <button
                  key={album.id}
                  onClick={() => loadPhotos(album.id)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    selectedAlbum?.id === album.id
                      ? "bg-amber-100 border-2 border-amber-300"
                      : "bg-slate-50 hover:bg-slate-100 border-2 border-transparent"
                  }`}
                >
                  <p className="font-medium text-slate-800">{album.name}</p>
                  <p className="text-xs text-slate-500">
                    {album.photo_count || 0} photos
                  </p>
                </button>
              ))}
              
              <Button variant="outline" className="w-full mt-4" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                New Album
              </Button>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="border-0 shadow-md mt-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{photos.length}</p>
                  <p className="text-xs text-slate-600">Photos</p>
                </div>
                <div className="text-center p-3 bg-rose-50 rounded-lg">
                  <p className="text-2xl font-bold text-rose-600">
                    {photos.filter(p => p.is_favorite).length}
                  </p>
                  <p className="text-xs text-slate-600">Favorites</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Photo Grid */}
        <div className="lg:col-span-3">
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {selectedAlbum ? selectedAlbum.name : "Select an Album"}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search photos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
                  >
                    {viewMode === "grid" ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!selectedAlbum ? (
                <div className="text-center py-12 text-slate-500">
                  <Folder className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Select an album to view photos</p>
                </div>
              ) : filteredPhotos.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No photos in this album yet</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowUpload(true)}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Photos
                  </Button>
                </div>
              ) : (
                <div className={`grid gap-4 ${
                  viewMode === "grid" 
                    ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4" 
                    : "grid-cols-1"
                }`}>
                  {filteredPhotos.map(photo => (
                    <div
                      key={photo.id}
                      className={`group relative rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-amber-300 transition-all ${
                        viewMode === "list" ? "flex gap-4 p-3 bg-slate-50" : ""
                      }`}
                      onClick={() => setSelectedPhoto(photo)}
                    >
                      <div className={viewMode === "grid" ? "aspect-square" : "w-24 h-24 flex-shrink-0"}>
                        <img
                          src={photo.url}
                          alt={photo.caption || "Family photo"}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400";
                          }}
                        />
                      </div>
                      
                      {/* Overlay for grid view */}
                      {viewMode === "grid" && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-white text-sm line-clamp-2">
                              {photo.caption || "No caption"}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(photo.id, photo.is_favorite);
                            }}
                            className="absolute top-2 right-2"
                          >
                            <Star className={`w-5 h-5 ${
                              photo.is_favorite 
                                ? "fill-amber-400 text-amber-400" 
                                : "text-white"
                            }`} />
                          </button>
                        </div>
                      )}
                      
                      {/* List view content */}
                      {viewMode === "list" && (
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 line-clamp-1">
                            {photo.caption || "No caption"}
                          </p>
                          {photo.ai_caption && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI Caption
                            </Badge>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {photo.tags?.slice(0, 3).map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Favorite indicator for list view */}
                      {viewMode === "list" && photo.is_favorite && (
                        <Star className="w-5 h-5 fill-amber-400 text-amber-400 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-500" />
              Upload Photos
            </DialogTitle>
            <DialogDescription>
              Add cherished memories to your gallery with AI-generated captions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              multiple
              className="hidden"
            />
            
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-amber-300 rounded-xl p-8 text-center cursor-pointer hover:bg-amber-50 transition-colors"
            >
              {uploading ? (
                <div className="space-y-3">
                  <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto" />
                  <p className="text-slate-600">Uploading and generating captions...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Image className="w-12 h-12 text-amber-400 mx-auto" />
                  <p className="font-medium text-slate-700">Click to select photos</p>
                  <p className="text-sm text-slate-500">or drag and drop here</p>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-800">AI Captions</span>
              </div>
              <p className="text-sm text-amber-700">
                Each photo will automatically receive a warm, loving caption 
                generated by AI to capture the emotion of the moment.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Detail Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-3xl">
          {selectedPhoto && (
            <>
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.caption}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800";
                  }}
                />
              </div>
              
              <div className="space-y-4">
                {/* Caption */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      Caption
                      {selectedPhoto.ai_caption && (
                        <Badge variant="outline" className="text-xs">
                          <Sparkles className="w-3 h-3 mr-1" />
                          AI Generated
                        </Badge>
                      )}
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => regenerateCaption(selectedPhoto.id)}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regenerate
                    </Button>
                  </div>
                  <p className="text-slate-700 italic">
                    &quot;{selectedPhoto.caption || "No caption"}&quot;
                  </p>
                </div>
                
                {/* Tags & People */}
                <div className="flex flex-wrap gap-2">
                  {selectedPhoto.tags?.map(tag => (
                    <Badge key={tag} variant="secondary">
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                  {selectedPhoto.people?.map(person => (
                    <Badge key={person} className="bg-blue-100 text-blue-700">
                      <Users className="w-3 h-3 mr-1" />
                      {person}
                    </Badge>
                  ))}
                </div>
                
                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleFavorite(selectedPhoto.id, selectedPhoto.is_favorite)}
                    >
                      <Star className={`w-4 h-4 mr-1 ${
                        selectedPhoto.is_favorite ? "fill-amber-400 text-amber-400" : ""
                      }`} />
                      {selectedPhoto.is_favorite ? "Favorited" : "Favorite"}
                    </Button>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deletePhoto(selectedPhoto.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Slideshow */}
      {showSlideshow && (
        <PhotoSlideshow
          photos={filteredPhotos}
          onClose={() => setShowSlideshow(false)}
        />
      )}
    </div>
  );
}

/**
 * Photo Slideshow Component
 */
function PhotoSlideshow({ photos, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showCaptions, setShowCaptions] = useState(true);

  useEffect(() => {
    if (!isPlaying) return;

    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % photos.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [isPlaying, photos.length]);

  const currentPhoto = photos[currentIndex];

  const goNext = () => setCurrentIndex((currentIndex + 1) % photos.length);
  const goPrev = () => setCurrentIndex((currentIndex - 1 + photos.length) % photos.length);

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="photo-slideshow">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent z-10">
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsPlaying(!isPlaying)}
              className="text-white hover:bg-white/20"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </Button>
            <span className="text-sm">
              {currentIndex + 1} / {photos.length}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-white/80">Captions</Label>
              <Switch
                checked={showCaptions}
                onCheckedChange={setShowCaptions}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Image */}
      <div className="flex-1 flex items-center justify-center relative">
        <button
          onClick={goPrev}
          className="absolute left-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
        >
          <ChevronLeft className="w-8 h-8 text-white" />
        </button>

        <div className="max-w-5xl max-h-[80vh] mx-auto">
          <img
            src={currentPhoto?.url}
            alt={currentPhoto?.caption}
            className="max-w-full max-h-[80vh] object-contain transition-opacity duration-500"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800";
            }}
          />
        </div>

        <button
          onClick={goNext}
          className="absolute right-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
        >
          <ChevronRight className="w-8 h-8 text-white" />
        </button>
      </div>

      {/* Caption */}
      {showCaptions && currentPhoto?.caption && (
        <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-xl text-center font-serif italic max-w-2xl mx-auto">
            &quot;{currentPhoto.caption}&quot;
          </p>
        </div>
      )}

      {/* Progress Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {photos.slice(0, 10).map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIndex ? "bg-white w-4" : "bg-white/50"
            }`}
          />
        ))}
        {photos.length > 10 && (
          <span className="text-white/50 text-xs">+{photos.length - 10}</span>
        )}
      </div>
    </div>
  );
}
